import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, Mail, Pencil, Phone, Plus, Search, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { SupplierDialog, type SupplierFormValues } from '../suppliers/SupplierDialog';
import { SupplierCategoryBadges } from '../suppliers/SupplierCategoryBadges';
import {
  SupplierCategoriesMultiSelect,
  type SupplierCategoryOption,
} from '../suppliers/SupplierCategoriesMultiSelect';
import { useSuppliersApi, useSupplierActions } from '../../services/suppliers';
import { useCategoriesApi } from '../../services/categories';
import type { Supplier } from '../../types/suppliers';

const UNCATEGORIZED_FILTER_VALUE = '__uncategorized__';
const UNCATEGORIZED_LABEL = 'Sin categoría';

function isReservedUncategorizedCategory(categoryName: string) {
  return categoryName.trim().localeCompare(UNCATEGORIZED_LABEL, 'es', { sensitivity: 'base' }) === 0;
}

function useDebounce<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function Suppliers() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [categoryFilterIds, setCategoryFilterIds] = useState<string[]>([]);
  const debouncedCategoryFilterIds = useDebounce(categoryFilterIds, 250);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notesPreview, setNotesPreview] = useState<Supplier | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [pageInput, setPageInput] = useState('1');
  const filterInitializedRef = useRef(false);
  const previousFilterOptionIdsRef = useRef<string[]>([]);
  const navigate = useNavigate();

  const {
    data: suppliers,
    loading,
    error,
    pagination,
    setFilters,
    setPagination,
    goToPage,
    refresh,
  } = useSuppliersApi({ search: undefined, active: true }, { page: 1, page_size: 50 });
  const { createSupplier, updateSupplier } = useSupplierActions();
  const { categories, loading: categoriesLoading } = useCategoriesApi({ active: true, limit: 200 });
  const assignableCategories = useMemo(
    () => categories.filter((category) => !isReservedUncategorizedCategory(category.name)),
    [categories],
  );
  const filterCategoryOptions = useMemo<SupplierCategoryOption[]>(
    () => [
      { label: UNCATEGORIZED_LABEL, value: UNCATEGORIZED_FILTER_VALUE },
      ...assignableCategories.map((category) => ({ label: category.name, value: category.id })),
    ],
    [assignableCategories],
  );
  const dialogCategoryOptions = useMemo<SupplierCategoryOption[]>(() => {
    const baseOptions = assignableCategories.map((category) => ({
      label: category.name,
      value: category.id,
      inactive: false,
    }));

    if (!selectedSupplier) {
      return baseOptions;
    }

    const baseOptionIds = new Set(baseOptions.map((option) => option.value));
    const missingOptions = selectedSupplier.category_ids
      .map((categoryId, index) => ({
        value: categoryId,
        label: selectedSupplier.categories[index] ?? 'Categoría',
        inactive: true,
      }))
      .filter((option) => !baseOptionIds.has(option.value));

    return [...baseOptions, ...missingOptions].sort((left, right) =>
      left.label.localeCompare(right.label, 'es', { sensitivity: 'base' }),
    );
  }, [assignableCategories, selectedSupplier]);

  useEffect(() => {
    const currentOptionIds = filterCategoryOptions.map((option) => option.value);
    const previousOptionIds = previousFilterOptionIdsRef.current;
    const currentOptionSet = new Set(currentOptionIds);

    setCategoryFilterIds((previousSelection) => {
      if (currentOptionIds.length === 0) {
        filterInitializedRef.current = false;
        previousFilterOptionIdsRef.current = [];
        return [];
      }

      if (!filterInitializedRef.current) {
        filterInitializedRef.current = true;
        previousFilterOptionIdsRef.current = currentOptionIds;
        return currentOptionIds;
      }

      const preservedSelection = previousSelection.filter((categoryId) =>
        currentOptionSet.has(categoryId),
      );
      const newOptionIds = currentOptionIds.filter(
        (categoryId) => !previousOptionIds.includes(categoryId),
      );
      const nextSelection = [...preservedSelection, ...newOptionIds];
      previousFilterOptionIdsRef.current = currentOptionIds;

      return nextSelection.length > 0 ? nextSelection : currentOptionIds;
    });
  }, [filterCategoryOptions]);

  useEffect(() => {
    const active = statusFilter === 'all' ? undefined : statusFilter === 'active';
    const allCategoryIds = filterCategoryOptions.map((option) => option.value);
    const selectedCategoryIdsSet = new Set(debouncedCategoryFilterIds);
    const allFiltersSelected =
      allCategoryIds.length > 0 && allCategoryIds.every((categoryId) => selectedCategoryIdsSet.has(categoryId));
    const selectedRealCategoryIds = filterCategoryOptions
      .filter(
        (option) =>
          option.value !== UNCATEGORIZED_FILTER_VALUE && selectedCategoryIdsSet.has(option.value),
      )
      .map((option) => option.value);
    const includeUncategorized = selectedCategoryIdsSet.has(UNCATEGORIZED_FILTER_VALUE);
    setFilters((prev) => {
      const next = {
        ...prev,
        search: debouncedSearch.trim() || undefined,
        active,
        category_ids:
          allFiltersSelected || selectedRealCategoryIds.length === 0
            ? undefined
            : selectedRealCategoryIds,
        include_uncategorized: allFiltersSelected ? undefined : includeUncategorized || undefined,
      };
      const same = prev.search === next.search
        && prev.active === next.active
        && prev.include_uncategorized === next.include_uncategorized
        && JSON.stringify(prev.category_ids ?? []) === JSON.stringify(next.category_ids ?? []);
      return same ? prev : next;
    });
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch, statusFilter, debouncedCategoryFilterIds, filterCategoryOptions, setFilters, setPagination]);

  const openCreate = () => {
    setSelectedSupplier(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const openNotesPreview = (supplier: Supplier) => {
    setNotesPreview(supplier);
    setNoteDraft(supplier.notes ?? '');
  };

  const goToSupplierItems = (supplierName: string) => {
    const trimmed = supplierName.trim();
    if (!trimmed) return;
    navigate(`/items?supplier=${encodeURIComponent(trimmed)}`, {
      state: { supplier: trimmed },
    });
  };

  const closeNotesPreview = () => {
    if (savingNote) return;
    setNotesPreview(null);
    setNoteDraft('');
  };

  const handleSave = async (values: SupplierFormValues) => {
    setSubmitting(true);
    const parsedDesvioComercial = Number(values.desvio_comercial_pct);
    const desvioComercialPct = Number.isFinite(parsedDesvioComercial)
      ? parsedDesvioComercial.toFixed(2)
      : '0.00';
    const parsedBeneficioComercial = Number(values.beneficio_comercial_pct);
    const beneficioComercialPct = Number.isFinite(parsedBeneficioComercial)
      ? parsedBeneficioComercial.toFixed(2)
      : '0.00';
    const payload = {
      name: values.name.trim(),
      contact: values.contact.trim() || null,
      email: values.email.trim().toLowerCase(),
      phone: values.phone.trim() || null,
      category_ids: values.selectedCategoryIds,
      desvio_comercial_pct: desvioComercialPct,
      beneficio_comercial_pct: beneficioComercialPct,
      notes: values.notes.trim() || null,
      active: values.active,
    };

    try {
      if (dialogMode === 'edit' && selectedSupplier) {
        await updateSupplier(selectedSupplier.id, payload);
        toast.success('Proveedor actualizado');
      } else {
        await createSupplier(payload);
        toast.success('Proveedor creado');
      }
      setDialogOpen(false);
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos guardar el proveedor';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNote = async () => {
    if (!notesPreview) return;
    setSavingNote(true);
    try {
      await updateSupplier(notesPreview.id, { notes: noteDraft.trim() || null });
      toast.success('Notas actualizadas');
      await refresh();
      closeNotesPreview();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos guardar la nota';
      toast.error(message);
    } finally {
      setSavingNote(false);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
    setCategoryFilterIds(filterCategoryOptions.map((option) => option.value));
  };

  const titleCount = pagination.total ?? suppliers.length;
  const showInitialLoading = loading && suppliers.length === 0;
  const totalPages = Math.max(
    1,
    pagination.total_pages ?? Math.ceil((pagination.total ?? suppliers.length) / pagination.page_size),
  );

  useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  const commitPageInput = () => {
    const parsed = Number(pageInput);
    if (!Number.isInteger(parsed)) {
      setPageInput(String(pagination.page));
      return;
    }
    const nextPage = Math.min(totalPages, Math.max(1, parsed));
    if (nextPage !== pagination.page) void goToPage(nextPage);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-gray-900 mb-2">Gestión de Proveedores</h1>
          <p className="text-gray-600">Directorio completo de proveedores y fabricantes</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate} disabled={loading || submitting}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por nombre, contacto o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)} disabled={loading}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Solo activos</SelectItem>
                <SelectItem value="inactive">Solo inactivos</SelectItem>
              </SelectContent>
            </Select>
            <div className="w-full sm:w-56">
              <SupplierCategoriesMultiSelect
                options={filterCategoryOptions}
                selectedValues={categoryFilterIds}
                placeholder="Todas las categorías"
                allSelectedLabel="Todas las categorías"
                emptyStateLabel="No hay categorías activas disponibles."
                disabled={categoriesLoading}
                ariaLabel="Filtrar proveedores por categorías"
                preventEmptySelection
                onChange={setCategoryFilterIds}
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleResetFilters} disabled={loading}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Proveedores {loading ? '(actualizando...)' : `(${titleCount})`}</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 p-4 text-sm text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
              <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>Reintentar</Button>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[12%] whitespace-normal">Nombre</TableHead>
                  <TableHead className="w-[12%] whitespace-normal">Contacto</TableHead>
                  <TableHead className="w-[12%] whitespace-normal">Teléfono</TableHead>
                  <TableHead className="w-[18%] whitespace-normal">Email</TableHead>
                  <TableHead className="w-[16%] whitespace-normal">Categorías</TableHead>
                  <TableHead className="text-center w-[7%]">Nº Items</TableHead>
                  <TableHead className="text-center w-[7%]" title="Desvío comercial (%)">
                    Desv.%
                  </TableHead>
                  <TableHead className="text-center w-[7%]" title="Beneficio comercial (%)">
                    Ben.%
                  </TableHead>
                  <TableHead className="text-center w-[6%]">Notas</TableHead>
                  <TableHead className="text-center w-[7%]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showInitialLoading ? (
                  Array.from({ length: 4 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell className="text-center"><Skeleton className="mx-auto h-5 w-12" /></TableCell>
                      <TableCell className="text-center"><Skeleton className="mx-auto h-5 w-16" /></TableCell>
                      <TableCell className="text-center"><Skeleton className="mx-auto h-5 w-16" /></TableCell>
                      <TableCell className="text-center"><Skeleton className="mx-auto h-5 w-24" /></TableCell>
                      <TableCell className="text-center"><Skeleton className="mx-auto h-5 w-16" /></TableCell>
                    </TableRow>
                  ))
                ) : suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-8 text-center text-gray-500">
                      No hay proveedores con los filtros actuales.
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((supplier) => {
                    const contactText = supplier.contact?.trim() ? supplier.contact : '-';
                    const phoneText = supplier.phone?.trim() ? supplier.phone : '-';
                    const desvioComercialValue = Number(supplier.desvio_comercial_pct ?? 0);
                    const desvioComercialText = Number.isFinite(desvioComercialValue)
                      ? desvioComercialValue.toFixed(2)
                      : '0.00';
                    const beneficioComercialValue = Number(supplier.beneficio_comercial_pct ?? 0);
                    const beneficioComercialText = Number.isFinite(beneficioComercialValue)
                      ? beneficioComercialValue.toFixed(2)
                      : '0.00';
                    return (
                      <TableRow key={supplier.id} className="hover:bg-gray-50">
                        <TableCell className="text-gray-900 whitespace-normal break-words">{supplier.name}</TableCell>
                        <TableCell className="text-gray-600 whitespace-normal break-words">{contactText}</TableCell>
                        <TableCell className="whitespace-normal break-words">
                          {phoneText === '-' ? (
                            <span className="text-gray-600">-</span>
                          ) : (
                            <div className="flex flex-wrap items-center gap-1 text-gray-600">
                              <Phone className="mr-2 h-4 w-4" />
                              <span>{phoneText}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-normal break-words">
                          <div className="flex flex-wrap items-center gap-1 text-blue-600">
                            <Mail className="mr-2 h-4 w-4" />
                            <a href={`mailto:${supplier.email ?? ''}`} className="hover:underline">
                              {supplier.email}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-normal break-words">
                          <SupplierCategoryBadges categories={supplier.categories} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2 py-1 h-auto"
                            title="Ver items de este proveedor"
                            onClick={() => goToSupplierItems(supplier.name)} // Dispara la navegación con el nombre del proveedor
                          >
                            <Badge variant="outline" className="px-2 py-1 text-xs">
                              {supplier.items_count}
                            </Badge>
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          {desvioComercialValue > 0 ? (
                            <span className="rounded-full bg-blue-100 px-2 py-1 text-sm text-blue-800">
                              {desvioComercialText}%
                            </span>
                          ) : (
                            <span className="text-gray-700">{desvioComercialText}%</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {beneficioComercialValue > 0 ? (
                            <span className="rounded-full bg-green-100 px-2 py-1 text-sm text-green-800">
                              {beneficioComercialText}%
                            </span>
                          ) : (
                            <span className="text-gray-700">{beneficioComercialText}%</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`inline-flex items-center gap-1 border px-2 py-1 ${
                              supplier.notes?.trim()
                                ? 'border-orange-300 bg-orange-50 text-orange-600'
                                : 'border-gray-200 bg-gray-50 text-gray-500'
                            }`}
                            title={supplier.notes ?? 'Sin notas'}
                            onClick={() => openNotesPreview(supplier)}
                          >
                            <StickyNote className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center align-middle">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(supplier)} disabled={submitting}>
                              Editar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1 || loading}>
              Anterior
            </Button>
            <div className="flex items-center gap-2">
              <Input
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitPageInput();
                }}
                className="h-9 w-16 text-center"
                disabled={loading}
              />
              <span className="text-sm text-gray-600">/ {totalPages}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(pagination.page + 1)}
              disabled={loading || pagination.page >= totalPages}
            >
              Siguiente
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(totalPages)}
              disabled={loading || pagination.page >= totalPages}
            >
              Última página
            </Button>
            <Select
              value={String(pagination.page_size)}
              onValueChange={(value) => {
                const nextSize = Number(value);
                setPagination((prev) => (prev.page_size === nextSize ? prev : { ...prev, page: 1, page_size: nextSize }));
              }}
              disabled={loading}
            >
              <SelectTrigger className="w-24">
                <SelectValue placeholder={String(pagination.page_size)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <SupplierDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={
          selectedSupplier
            ? {
                name: selectedSupplier.name,
                contact: selectedSupplier.contact ?? '',
                email: selectedSupplier.email ?? '',
                phone: selectedSupplier.phone ?? '',
                selectedCategoryIds: selectedSupplier.category_ids ?? [],
                desvio_comercial_pct: String(selectedSupplier.desvio_comercial_pct ?? ''),
                beneficio_comercial_pct: String(selectedSupplier.beneficio_comercial_pct ?? ''),
                notes: selectedSupplier.notes ?? '',
                active: selectedSupplier.active,
              }
            : undefined
        }
        isSubmitting={submitting}
        categoryOptions={dialogCategoryOptions}
        categoryLoading={categoriesLoading}
        onSubmit={handleSave}
        onClose={() => setDialogOpen(false)}
      />

      <AlertDialog open={Boolean(notesPreview)} onOpenChange={(open) => { if (!open && !savingNote) closeNotesPreview(); }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Notas para {notesPreview?.name}</AlertDialogTitle>
            <AlertDialogDescription>Escribe o edita las observaciones internas del proveedor.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <Textarea
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              rows={5}
              disabled={savingNote}
              placeholder="Observaciones internas sobre el proveedor"
              className="min-h-[240px] max-h-[400px] resize-y overflow-x-hidden break-all whitespace-pre-wrap"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingNote} onClick={closeNotesPreview}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              disabled={savingNote || noteDraft === (notesPreview?.notes ?? '')}
              onClick={(event) => {
                event.preventDefault();
                void handleSaveNote();
              }}
            >
              {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
